import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getModules } from '../lib/modulesApi';
import type { ModuleKey } from '../lib/moduleCatalog';
import { useAuthStore } from '../store/useAuthStore';

export const MODULES_KEY = ['modules'] as const;

export interface UseModulesResult {
  /** Whether a product module is on for this instance (true while loading). */
  isOn: (key: ModuleKey) => boolean;
  /** Every module that is off (console ceiling, Settings → Module, or parent off). */
  off: ReadonlySet<ModuleKey>;
  isLoading: boolean;
}

/**
 * The product modules that are on (lib/moduleCatalog.ts). The backend already
 * masks off modules in /api/me/permissions; this is for the parts with no
 * permission flag (team chat, reminders, calendar, MCP, client tabs, the
 * Financiar sub-pages), the route guard, and the admin bypass in
 * usePermissions. Refetched every 5 minutes so a change made in the Firmio
 * Console lands without a reload.
 */
export function useModules(): UseModulesResult {
  const isAuthenticated = Boolean(useAuthStore((s) => s.user?.id));
  const { data, isLoading } = useQuery({
    queryKey: MODULES_KEY,
    queryFn: getModules,
    enabled: isAuthenticated,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    refetchIntervalInBackground: false,
  });
  const off = useMemo(
    () => new Set((data?.modules ?? []).filter((m) => !m.enabled).map((m) => m.key)),
    [data],
  );
  const isOn = useCallback((key: ModuleKey) => !off.has(key), [off]);
  return { isOn, off, isLoading: isAuthenticated && isLoading };
}
